import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';

import { useSiteConfig } from 'hooks/useSiteConfig';
import { usePixleeConfigQuery } from '__generated__/graphql';
import { isServer } from 'utils/constants';
import { useTranslate } from 'hooks/useTranslations';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { event, AnalyticsEvents } from 'utils/analytics';
import { usePromotionView } from 'hooks/usePromotionView';
const PIXLEE_PRODUCT_WIDGET_DEFAULT_CONTAINER_ID = 'pixlee_container';

type PixleeProductWidgetProps = {
  containerId?: string;
  skuId: string;
  widgetId?: string;
};

const addPixleeWidget = ({ containerId, skuId, widgetId, receiveMessage }) => {
  window.Pixlee.addProductWidget({
    AB: false,
    accountId: '1372',
    containerId,
    setMetaTags: false,
    skuId,
    subscribedEvents: ['photoOpened', 'widgetNumPhotos', 'ctaClicked'],
    widgetId,
  });
  window.Pixlee.resizeWidget();
  window.addEventListener('message', receiveMessage, false);
};

const initPixlee = (apiKey: string) => {
  window.PixleeAsyncInit = () => {
    window.Pixlee.init({ apiKey });
  };
};

export const PixleeProductWidget = ({
  containerId = PIXLEE_PRODUCT_WIDGET_DEFAULT_CONTAINER_ID,
  skuId,
}: PixleeProductWidgetProps) => {
  const t = useTranslate();
  const { pageviewEventHasFired } = usePageEventsContext();

  const { env, language, pixleeId } = useSiteConfig();
  const [pixleeConfigResponse] = usePixleeConfigQuery();

  const pixleeConfig = pixleeConfigResponse.data?.pixleeConfig;
  const widgetId = pixleeId?.[language];
  const shouldInitPixlee = !isServer && widgetId && pixleeConfig?.enable;
  const [widgetAdded, setWidgetAdded] = useState(false);
  const { setPixleeProductDiscovery } = usePageEventsContext();
  const pixleeData = useRef({
    item_id_ep: '',
    item_name_ep: '',
  });

  const pixleeProductWidgetRef = useRef(null);
  const gaTrackData = {
    id: skuId,
    promotion_id: widgetId || '',
    name: 'As Worn By You',
    creative: 'pixlee',
    creative_name: 'PixleeSimpleWidget',
    position: '1',
  };
  usePromotionView(pixleeProductWidgetRef, gaTrackData);

  const receiveMessage = e => {
    if (!e) return;
    if (e.data) {
      try {
        const message = JSON.parse(e.data);
        const photoOpenedMessage = message.eventName === 'photoOpened';

        if (photoOpenedMessage && message?.data?.products[0]) {
          pixleeData.current = {
            item_id_ep: message?.data?.products[0].sku,
            item_name_ep: message?.data?.products[0].title,
          };
        }
        const { item_id_ep, item_name_ep } = pixleeData.current;

        if (message.eventName && message.eventName === 'ctaClicked') {
          if (pageviewEventHasFired) {
            event(AnalyticsEvents.GA4_CustomEvent, {
              event_name: AnalyticsEvents.PIXLEE,
              event_params: {
                user_action: AnalyticsEvents.GA4_PRODUCT_CLICK,
                item_id_ep,
                item_name_ep,
              },
            });
          }

          setPixleeProductDiscovery(true);
        }

        if (message.eventName && photoOpenedMessage) {
          const photoIndex = message.data.photoIndex.toString();

          if (pageviewEventHasFired) {
            event(AnalyticsEvents.GA4_CustomEvent, {
              event_name: AnalyticsEvents.PIXLEE,
              event_params: {
                user_action: 'pixlee_open',
                item_id_ep,
                item_name_ep,
              },
            });
          }

          event(AnalyticsEvents.PROMOTION_CLICK, {
            ecommerce: {
              promoClick: {
                promotions: [
                  {
                    id: skuId,
                    name: 'As Worn By You',
                    creative: 'pixlee',
                    position: photoIndex,
                  },
                ],
              },
            },
          });
        }
      } catch (error) {
        return;
      }
    }
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('message', receiveMessage, false);
      window.Pixlee?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!shouldInitPixlee) return null;

  return (
    <>
      <Script
        src="https://assets.pxlecdn.com/assets/pixlee_widget_1_0_0.js"
        onReady={() => {
          initPixlee(env.pixleeApiKey);

          if (!widgetAdded) {
            addPixleeWidget({
              containerId,
              skuId,
              widgetId,
              receiveMessage,
            });
            setWidgetAdded(true);
          }
        }}
      />
      <section
        data-test-id="pixlee-product-widget"
        aria-label={t('pixleeSectionTitle')}
        id={containerId}
        ref={pixleeProductWidgetRef}
      />
    </>
  );
};
